import React, { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  Text,
  Box,
  Button,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Input,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';

import {
  DownloadIcon,
  DeleteIcon,
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@chakra-ui/icons';

import Card from 'components/card/Card';

import { useAppSelector, useAppDispatch } from 'hooks';

import {
  apiGetSupplementsList,
  apiDownloadSupplement,
  apiUploadSupplementFile,
  apiDeleteSupplementFile,
} from 'api';

import {
  addSupplement,
  removeSupplement,
} from 'reducers/homePersonalFormReducer';

import type { APISupplementOption, APIsupplementFile } from 'types/api.types';
import useIsBinderDisabled from 'hooks/useFormBinderDisabled';

const Supplements = () => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDisabled = useIsBinderDisabled('homePersonalForm');
  const { isOpen: isAvailableOpen, onToggle: onAvailableToggle } =
    useDisclosure();

  const uuid = useAppSelector((state) => state.homePersonalForm.uuid);

  const insuranceCompany = useAppSelector(
    (state) =>
      state.homePersonalForm.data?.policy_information?.insurance_company,
  );

  const uploadedSupplements = useAppSelector(
    (state) => state.homePersonalForm.supplements,
  );

  const {
    data: availableSupplements = [],
    isLoading,
    error,
  } = useQuery<APISupplementOption[]>({
    queryKey: ['supplements', insuranceCompany],
    queryFn: () => apiGetSupplementsList(insuranceCompany || ''),
    enabled: !!insuranceCompany,
    refetchOnWindowFocus: false,
  });

  const handleDownload = async (
    supplementId: string,
    supplementName: string,
  ) => {
    try {
      await apiDownloadSupplement(supplementId);
      toast({
        title: 'Download successful',
        description: `${supplementName} has been downloaded.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Download failed',
        description: error?.message || 'Failed to download supplement.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !uuid) {
      return;
    }

    if (file.type !== 'application/pdf') {
      toast({
        title: 'Invalid file type',
        description: 'Only PDF files are allowed.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiUploadSupplementFile(uuid, formData);

      dispatch(addSupplement(response));

      toast({
        title: 'Upload successful',
        description: `${file.name} has been uploaded.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description:
          error?.response?.data?.error || 'Failed to upload supplement.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (supplement: APIsupplementFile) => {
    if (!uuid) return;

    try {
      await apiDeleteSupplementFile(uuid, supplement.id);

      dispatch(removeSupplement(supplement.id));

      toast({
        title: 'Delete successful',
        description: `${supplement.name} has been deleted.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description:
          error?.response?.data?.error || 'Failed to delete supplement.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDownloadUploaded = (supplement: APIsupplementFile) => {
    try {
      // Open the supplement in a new tab
      window.open(supplement.file, '_blank');

      toast({
        title: 'File opened',
        description: `${supplement.name} has been opened in a new tab.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to open file',
        description: 'Failed to open supplement in new tab.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Card mb="20px" id="sectionSupplements">
      <Text fontWeight="bold" fontSize="xl" mb="20px">
        Supplements
      </Text>

      {/* Uploaded Supplements Section */}
      <Box mb="6">
        <HStack justify="space-between" align="center" mb="4">
          <Text fontWeight="semibold" fontSize="l">
            Uploaded Supplement
          </Text>

          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            leftIcon={<AddIcon />}
            onClick={handleFileUpload}
            disabled={isDisabled}
          >
            Upload PDF
          </Button>
        </HStack>

        <Input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {uploadedSupplements.length === 0 ? (
          <Text fontSize="md" color="gray.600" fontStyle="italic">
            No supplements uploaded yet.
          </Text>
        ) : (
          <VStack spacing="3" align="stretch">
            {uploadedSupplements.map((supplement) => (
              <Box
                key={supplement.id}
                p="4"
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="blue.50"
                _hover={{ bg: 'blue.100' }}
              >
                <HStack justify="space-between" align="center">
                  <VStack align="start" spacing="1">
                    <Text fontWeight="medium">{supplement.name}</Text>
                    <Text fontSize="sm" color="gray.600">
                      Uploaded on{' '}
                      {new Date(supplement.created_at).toLocaleDateString()}
                    </Text>
                  </VStack>

                  <HStack spacing="2">
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      leftIcon={<DownloadIcon />}
                      onClick={() => handleDownloadUploaded(supplement)}
                    >
                      Download
                    </Button>

                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      leftIcon={<DeleteIcon />}
                      onClick={() => handleDelete(supplement)}
                    >
                      Delete
                    </Button>
                  </HStack>
                </HStack>
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      {/* Available Supplements Section */}
      <Box>
        <Button
          variant="ghost"
          onClick={onAvailableToggle}
          rightIcon={isAvailableOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          fontWeight="semibold"
          fontSize="l"
          p="0"
          h="auto"
          justifyContent="flex-start"
          mb="4"
          _hover={{ bg: 'transparent', color: 'blue.500' }}
        >
          Available supplements for {insuranceCompany}
        </Button>

        <Collapse in={isAvailableOpen} animateOpacity>
          <Box>
            {!insuranceCompany ? (
              <Alert status="info">
                <AlertIcon />
                Please select an insurance company to view available
                supplements.
              </Alert>
            ) : (
              <>
                {isLoading && (
                  <Box textAlign="center" py="4">
                    <Spinner size="md" />
                    <Text mt="2">Loading supplements...</Text>
                  </Box>
                )}

                {error && (
                  <Alert status="error" mb="4">
                    <AlertIcon />
                    Failed to load supplements. Please try again later.
                  </Alert>
                )}

                {!isLoading && !error && availableSupplements.length === 0 && (
                  <Alert status="info">
                    <AlertIcon />
                    No supplements available for {insuranceCompany}.
                  </Alert>
                )}

                {!isLoading && !error && availableSupplements.length > 0 && (
                  <VStack spacing="3" align="stretch">
                    {availableSupplements.map((supplement) => (
                      <Box
                        key={supplement.id}
                        p="4"
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        bg="gray.50"
                        _hover={{ bg: 'gray.100' }}
                      >
                        <HStack justify="space-between" align="center">
                          <Text fontWeight="medium">{supplement.name}</Text>

                          <Button
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            leftIcon={<DownloadIcon />}
                            onClick={() =>
                              handleDownload(supplement.id, supplement.name)
                            }
                          >
                            Download
                          </Button>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </>
            )}
          </Box>
        </Collapse>
      </Box>
    </Card>
  );
};

export default Supplements;
